/**
 * DegreeDaysCharts — synchronized HDD65 and CDD65 annual charts.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import type { DegreeDayAnnualRow, DegreeDayStatewideRow } from '../../services/degreeDaysDataService';

Chart.register(...registerables);

interface DegreeDaysChartsProps {
  citySeries: DegreeDayAnnualRow[];
  statewideSeries: DegreeDayStatewideRow[];
  normalHdd65: number | null;
  normalCdd65: number | null;
  selectedYear: number;
  locationLabel: string;
}

const CITY_COLOR = '#2563eb';
const BENCHMARK_COLOR = '#64748b';
const NORMAL_COLOR = '#94a3b8';
const HIGHLIGHT_COLOR = '#dc2626';

function buildChart(
  canvas: HTMLCanvasElement,
  title: string,
  cityValues: (number | null)[],
  benchmarkValues: (number | null)[],
  normalValue: number | null,
  labels: string[],
  selectedYear: number,
  unitLabel: string
): Chart {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas context missing');

  const highlightRadii = labels.map((l) => (Number(l) === selectedYear ? 6 : 3));
  const highlightColors = labels.map((l) =>
    Number(l) === selectedYear ? HIGHLIGHT_COLOR : CITY_COLOR
  );

  const datasets: any[] = [
    {
      label: 'Selected city',
      data: cityValues,
      borderColor: CITY_COLOR,
      backgroundColor: 'rgba(37,99,235,0.15)',
      pointBackgroundColor: highlightColors,
      pointBorderColor: highlightColors,
      pointRadius: highlightRadii,
      pointHoverRadius: 7,
      borderWidth: 2,
      tension: 0,
      spanGaps: false,
    },
    {
      label: 'California incorporated-city average',
      data: benchmarkValues,
      borderColor: BENCHMARK_COLOR,
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [4, 4],
      pointRadius: 0,
      tension: 0,
      spanGaps: false,
    },
  ];

  const plugins: any = {
    legend: { display: true, position: 'bottom' as const },
    title: {
      display: true,
      text: title,
      font: { size: 14, weight: '600' },
    },
    tooltip: { mode: 'index' as const, intersect: false },
  };

  if (normalValue != null) {
    plugins.annotation = undefined;
  }

  const chart = new Chart(ctx, {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        ...plugins,
        subtitle: normalValue != null
          ? {
              display: true,
              text: `1991–2020 normal: ${Math.round(normalValue).toLocaleString()} ${unitLabel} (dashed reference in chart area)`,
              font: { size: 11 },
              color: '#6b7280',
            }
          : undefined,
      },
      scales: {
        x: {
          title: { display: true, text: 'Year' },
          ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
        },
        y: {
          title: { display: true, text: unitLabel },
          beginAtZero: true,
        },
      },
    },
    plugins: normalValue != null
      ? [
          {
            id: 'normalLine',
            afterDraw(c: Chart) {
              const yScale = c.scales.y;
              const xScale = c.scales.x;
              if (!yScale || !xScale) return;
              const y = yScale.getPixelForValue(normalValue);
              const ctx2 = c.ctx;
              ctx2.save();
              ctx2.setLineDash([6, 4]);
              ctx2.strokeStyle = NORMAL_COLOR;
              ctx2.lineWidth = 1.5;
              ctx2.beginPath();
              ctx2.moveTo(xScale.left, y);
              ctx2.lineTo(xScale.right, y);
              ctx2.stroke();
              ctx2.fillStyle = NORMAL_COLOR;
              ctx2.font = '11px sans-serif';
              ctx2.fillText(`Normal ${Math.round(normalValue)}`, xScale.right - 72, y - 4);
              ctx2.restore();
            },
          },
        ]
      : [],
  });

  return chart;
}

const DegreeDaysCharts: React.FC<DegreeDaysChartsProps> = ({
  citySeries,
  statewideSeries,
  normalHdd65,
  normalCdd65,
  selectedYear,
  locationLabel,
}) => {
  const hddRef = useRef<HTMLCanvasElement>(null);
  const cddRef = useRef<HTMLCanvasElement>(null);
  const hddChart = useRef<Chart | null>(null);
  const cddChart = useRef<Chart | null>(null);

  const destroy = useCallback(() => {
    hddChart.current?.destroy();
    cddChart.current?.destroy();
    hddChart.current = null;
    cddChart.current = null;
  }, []);

  useEffect(() => {
    if (!hddRef.current || !cddRef.current || citySeries.length === 0) return;
    destroy();

    const labels = citySeries.map((r) => String(r.year));
    const hddCity = citySeries.map((r) => r.hdd65_actual);
    const cddCity = citySeries.map((r) => r.cdd65_actual);
    const byYear = new Map(statewideSeries.map((r) => [r.year, r]));
    const hddBench = citySeries.map((r) => byYear.get(r.year)?.mean_hdd65_actual ?? null);
    const cddBench = citySeries.map((r) => byYear.get(r.year)?.mean_cdd65_actual ?? null);

    hddChart.current = buildChart(
      hddRef.current,
      `Heating Degree Days — HDD65 (${locationLabel})`,
      hddCity,
      hddBench,
      normalHdd65,
      labels,
      selectedYear,
      'degree days, base 65°F'
    );
    cddChart.current = buildChart(
      cddRef.current,
      `Cooling Degree Days — CDD65 (${locationLabel})`,
      cddCity,
      cddBench,
      normalCdd65,
      labels,
      selectedYear,
      'degree days, base 65°F'
    );

    return destroy;
  }, [citySeries, statewideSeries, normalHdd65, normalCdd65, selectedYear, locationLabel, destroy]);

  if (citySeries.length === 0) {
    return (
      <div style={{ padding: 40, color: '#6b7280', textAlign: 'center' }}>
        No annual degree day data available for this location.
      </div>
    );
  }

  return (
    <div>
      <div
        className="degree-days-charts-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 24,
        }}
      >
        <div style={{ height: 320 }}>
          <canvas ref={hddRef} aria-label={`Heating degree days chart for ${locationLabel}`} />
        </div>
        <div style={{ height: 320 }}>
          <canvas ref={cddRef} aria-label={`Cooling degree days chart for ${locationLabel}`} />
        </div>
      </div>
      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 12 }}>
        Source: PRISM Climate Group daily mean temperature (800 m stable, IDW), 2000–2024. California
        incorporated-city average is the arithmetic mean of 482 Census 2020 incorporated-place
        representative points.
      </p>
    </div>
  );
};

export default DegreeDaysCharts;
